import {
	TemplatePhotoPosition,
	TemplateSet,
	TemplateStateModel,
	ThemeTemplateSet,
} from '@sosocio/creator-types/app';
import * as DB from '@sosocio/creator-types/database';
import * as PI from '@sosocio/creator-types/project';
import Template, { DEFAULT_DYNAMIC_TEMPLATE_ID } from 'classes/template';
import { ERRORS_INVALID_REQUEST_DATA } from 'settings/errors';
import {
	AppDataModule,
	ProductStateModule,
	ThemeDataModule,
} from 'store';
import getFixedTemplateSets from 'tools/get-fixed-template-sets';
import _ from 'underscore';
import { reactive } from 'vue';
import {
	Action,
	Module,
	Mutation,
	VuexModule,
} from 'vuex-module-decorators';
import { resolveThemePageStateModel } from './page-state';

/**
 * Adds the canonical photo-slot metadata to a complete generated layout.
 *
 * @param templateId Template owning the layout
 * @param dynamicTemplate Generated geometry and its exact saved id
 * @returns A complete template set suitable for placement or persisted recovery
 */
function createDynamicTemplateSet(
	templateId: DB.TemplateModel['id'],
	dynamicTemplate: ReturnType<typeof Template.getDynamicTemplates>[number],
): TemplateSet {
	const positions = dynamicTemplate.positions.map(
		(p) => {
			const photoPosition: TemplatePhotoPosition = {
				id: p.id,
				x: p.x,
				y: p.y,
				z: p.z,
				width: p.width,
				height: p.height,
				angle: 0,
				borderwidth: 0,
				bordercolor: '#000000',
				transformable: true,
				available: true,
				type: 'photo',
				mask: null,
				flag: true,
				autorotate: false,
				styleable: true,
				overlap_top: 0,
				overlap_right: 0,
				overlap_bottom: 0,
				overlap_left: 0,
				fillMethod: 'cover',
			};

			return photoPosition;
		},
	);

	return {
		id: dynamicTemplate.id,
		templateId,
		positions,
	};
}

@Module({
	namespaced: true,
	name: 'themestate',
})
export default class ThemeState extends VuexModule {
	public borderImageCollection = reactive<DB.BorderImageModel[]>([]);

	public layoutCollection = reactive<DB.LayoutModel[]>([]);

	public layoutTemplateLinkCollection = reactive<DB.LayoutTemplateLinkModel[]>([]);

	public pageStateCollection = reactive<PI.PageStateModel[]>([]);

	public themeLayoutLinkCollection = reactive<DB.ThemeLayoutLinkModel[]>([]);

	/**
	 * If the theme model changes, reset the theme template sets
	 */
	public themeModel: DB.ThemeModel | null = null;

	public themeTemplateSets = reactive<ThemeTemplateSet[]>([]);

	public get getTemplateSets() {
		return (
			templateId: DB.TemplateModel['id'],
			options?: {
				photoCount?: number,
				marginAroundEdge?: number,
				marginBetweenPositions?: number,
				dpi?: number,
			},
		): TemplateSet[] => {
			const templateModel = ThemeDataModule.getTemplate(templateId);

			if (!templateModel) {
				throw new Error('Could not find template model');
			}

			const sets: TemplateSet[] = getFixedTemplateSets(templateId);

			if (!templateModel.dynamic) {
				return sets;
			}

			if (
				!templateModel.width
				|| !templateModel.height
			) {
				throw new Error('Template model has no width or height');
			}

			const themeTemplateSetsFound = this.themeTemplateSets.find((themeTemplateSet) => {
				if (
					themeTemplateSet.templateId === templateModel.id
					&& themeTemplateSet.photoCount === options?.photoCount
					&& themeTemplateSet.marginAroundEdge === options?.marginAroundEdge
					&& themeTemplateSet.marginBetweenPositions === options?.marginBetweenPositions
					&& themeTemplateSet.dpi === options?.dpi
				) {
					return true;
				}

				return false;
			});

			if (themeTemplateSetsFound) {
				return themeTemplateSetsFound.templateSets;
			}

			const photoQuantities: number[] = (
				options?.photoCount
					? [options?.photoCount]
					: [
						1,
						2,
						3,
						4,
						5,
					]
			);
			const dynamicTemplates = Template.getDynamicTemplates({
				templateData: {
					id: templateModel.id,
					width: templateModel.width,
					height: templateModel.height,
					bleedMargin: templateModel.bleedmargin ?? 0,
					marginAroundEdge: options?.marginAroundEdge ?? templateModel.dynamicPageMargin ?? 0,
					marginBetweenPositions: options?.marginBetweenPositions ?? templateModel.dynamicPagePadding ?? 0,
				},
				positionQuantities: photoQuantities,
				dpi: options?.dpi,
			});

			sets.push(...dynamicTemplates.map((dynamicTemplate) => createDynamicTemplateSet(
				templateModel.id,
				dynamicTemplate,
			)));

			this.themeTemplateSets.push({
				templateId: templateModel.id,
				photoCount: options?.photoCount,
				marginAroundEdge: options?.marginAroundEdge,
				marginBetweenPositions: options?.marginBetweenPositions,
				dpi: options?.dpi,
				templateSets: sets,
			});

			return sets;
		};
	}

	public get getTemplateSet() {
		return (
			templateId: DB.TemplateModel['id'],
			photoModels?: PI.PhotoModel[],
			options?: {
				marginAroundEdge?: number;
				marginBetweenPositions?: number;
				dpi?: number;
			},
		): {
			templateSet: TemplateSet;
			photoModels: PI.PhotoModel[] | undefined;
		} => {
			const templateModel = ThemeDataModule.getTemplate(
				templateId,
			);

			if (!templateModel) {
				throw new Error('Could not find template model');
			}

			if (
				templateModel.dynamic
				&& photoModels?.length
			) {
				if (
					!templateModel.width
					|| !templateModel.height
				) {
					throw new Error('Template model has no width or height');
				}

				const photoData = photoModels.map((photoModel) => ({
					id: photoModel.id,
					width: photoModel.full_width,
					height: photoModel.full_height,
				}));

				const dynamicTemplate = Template.getTemplatePositions({
					templateData: {
						id: templateModel.id,
						width: templateModel.width,
						height: templateModel.height,
						bleedMargin: templateModel.bleedmargin ?? 0,
						marginAroundEdge: options?.marginAroundEdge ?? (templateModel.dynamicPageMargin || 0),
						marginBetweenPositions: options?.marginBetweenPositions ?? (templateModel.dynamicPagePadding || 0),
					},
					photoData,
					randomness: 0.1,
					dpi: options?.dpi,
				});

				const positions = dynamicTemplate.positions.map((p) => {
					const photoPosition: TemplatePhotoPosition = {
						id: p.id,
						x: p.x,
						y: p.y,
						z: p.z,
						width: p.width,
						height: p.height,
						angle: 0,
						borderwidth: 0,
						bordercolor: '#000000',
						transformable: true,
						available: true,
						type: 'photo',
						mask: null,
						flag: true,
						autorotate: false,
						styleable: true,
						overlap_top: 0,
						overlap_right: 0,
						overlap_bottom: 0,
						overlap_left: 0,
						fillMethod: 'cover',
					};

					return photoPosition;
				});
				const photos = dynamicTemplate.orderedPhotoModels
					.map((m) => (
						photoModels.find((p) => p.id === m.id)
					))
					.filter((photo): photo is PI.PhotoModel => !!photo);

				return {
					templateSet: {
						id: positions
							.map((position) => position.id)
							.join('_'),
						templateId: templateModel.id,
						positions,
					},
					photoModels: photos,
				};
			}

			const templateSets = this.getTemplateSets(
				templateId,
				{
					marginAroundEdge: options?.marginAroundEdge,
					marginBetweenPositions: options?.marginBetweenPositions,
					dpi: options?.dpi,
				},
			);

			if (!templateSets.length) {
				return {
					templateSet: {
						id: 'empty',
						templateId,
						positions: [],
					},
					photoModels,
				};
			}

			const templateSetProperties = templateSets[0];
			return {
				templateSet: {
					...templateSetProperties,
					id: templateSetProperties.positions.map(
						(position) => position.id,
					).join('_'),
				},
				photoModels,
			};
		};
	}

	public get getFixedPages() {
		const pages = _.filter(
			this.pageStateCollection,
			(pageStateModel) => pageStateModel.serialnumber < 0,
		);

		return _.sortBy(
			pages,
			'serialnumber',
		);
	}

	public get getVirtualPageStateData() {
		return (
			offeringId: DB.OfferingModel['id'],
			pageOrientation?: PI.PageModel['orientation'],
		) => {
			const activeOfferingModel = AppDataModule.getOffering(
				offeringId,
			);

			if (pageOrientation === 'p') {
				return this.pageStateCollection.find(
					(pageStateModel) => Boolean(
						activeOfferingModel
						&& pageStateModel.pageModel.width == activeOfferingModel.height
						&& pageStateModel.pageModel.height == activeOfferingModel.width,
					),
				);
			}

			return this.pageStateCollection.find(
				(pageStateModel) => Boolean(
					activeOfferingModel
					&& pageStateModel.pageModel.width == activeOfferingModel.width
					&& pageStateModel.pageModel.height == activeOfferingModel.height,
				),
			);
		};
	}

	public get getVirtualLayoutTemplateModels() {
		return (
			offeringId: DB.OfferingModel['id'],
			pageOrientation?: PI.PageModel['orientation'],
		) => {
			const activeOfferingModel = AppDataModule.getOffering(
				offeringId,
			);

			const layoutTemplateLinkModels = this.layoutTemplateLinkCollection.filter(
				(m) => {
					const templateModel = ThemeDataModule.getTemplate(
						m.templateid,
					);

					if (!templateModel) {
						return false;
					}

					if (
						pageOrientation
						&& pageOrientation === 'p'
					) {
						return (
							activeOfferingModel
							&& templateModel.width == activeOfferingModel.height
							&& templateModel.height == activeOfferingModel.width
						);
					}

					return (
						activeOfferingModel
						&& templateModel.width == activeOfferingModel.width
						&& templateModel.height == activeOfferingModel.height
					);
				},
			);

			return layoutTemplateLinkModels;
		};
	}

	public get getPageStateData() {
		return (
			pageIndex: number,
			offset: number,
			options: {
				layoutId?: number;
				offeringId?: DB.OfferingModel['id'];
				photoOrientation?: PI.PageModel['orientation'];
			},
		): PI.PageStateModel => {
			const allPages: PI.PageStateModel[] = [];
			const offeringModel = ProductStateModule.getOffering;
			let childOfferingModel: DB.OfferingModel | undefined;
			let pageOrientation: PI.PageModel['orientation'] | undefined;

			if (offeringModel?.virtual) {
				const childOfferingId = options.offeringId || ProductStateModule.getChildOfferingId;
				childOfferingModel = (
					childOfferingId
						? AppDataModule.getOffering(childOfferingId)
						: undefined
				);

				if (
					options.photoOrientation === 'p'
					&& childOfferingModel
					&& childOfferingModel.width > childOfferingModel.height
				) {
					pageOrientation = 'p';
				}
			}

			if (childOfferingModel) {
				const virtualPageState = this.getVirtualPageStateData(
					childOfferingModel.id,
					pageOrientation,
				);

				if (virtualPageState) {
					allPages.push(virtualPageState);
				}
			} else {
				const filteredPages = this.pageStateCollection.filter(
					(pageStateModel) => pageStateModel.serialnumber >= 0,
				);
				allPages.push(...filteredPages);
			}

			const pageStateModel = resolveThemePageStateModel({
				allPages,
				offset,
				pageIndex,
			});

			const pageStateData: PI.PageStateModel = JSON.parse(JSON.stringify(pageStateModel));

			if (
				childOfferingModel
				&& pageOrientation === 'p'
			) {
				pageStateData.pageModel.orientation = 'p';
			}

			if (options.layoutId) {
				const allLayoutTemplateLinkModels: DB.LayoutTemplateLinkModel[] = [];

				if (
					offeringModel
					&& offeringModel.virtual
					&& childOfferingModel
				) {
					const virtualLayoutTemplates = this.getVirtualLayoutTemplateModels(
						childOfferingModel.id,
						pageOrientation,
					);
					allLayoutTemplateLinkModels.push(
						...virtualLayoutTemplates,
					);
				} else {
					allLayoutTemplateLinkModels.push(
						...this.layoutTemplateLinkCollection,
					);
				}

				const layoutTemplateLinkModels = _.where(
					allLayoutTemplateLinkModels,
					{
						layoutid: options.layoutId,
					},
				);
				let layoutTemplateModel = _.findWhere(
					layoutTemplateLinkModels,
					{
						serialnumber: pageIndex,
					},
				);

				if (
					!layoutTemplateModel
					&& pageStateModel.repeat
				) {
					const sortedLayoutTemplates = _.sortBy(
						layoutTemplateLinkModels,
						'serialnumber',
					);
					const repeatLayoutTemplates = sortedLayoutTemplates.filter(
						(sortedLayoutTemplateModel) => Boolean(sortedLayoutTemplateModel.repeat),
					);

					const nr = offset - (sortedLayoutTemplates.length - repeatLayoutTemplates.length);
					const modulus = (nr + repeatLayoutTemplates.length) % repeatLayoutTemplates.length;
					layoutTemplateModel = repeatLayoutTemplates[modulus];
				}

				if (layoutTemplateModel && layoutTemplateModel.templateid) {
					pageStateData.pageModel.template = layoutTemplateModel.templateid;
				}
			}

			return pageStateData;
		};
	}

	public get getRepeatPages(): PI.PageStateModel[] {
		const pages = this.pageStateCollection.filter(
			(pageStateModel) => pageStateModel.repeat,
		);

		return pages.sort(
			(a, b) => a.serialnumber - b.serialnumber,
		);
	}

	/**
	 * The distinct configdpi values of the loaded offerings, used to resolve
	 * template set ids that were saved at another offering dpi.
	 */
	public get offeringConfigDpis(): number[] {
		return _.uniq(
			AppDataModule.offerings.map((offeringModel) => offeringModel.configdpi),
		);
	}

	public get getTemplateSetById() {
		return (
			templateId: DB.TemplateModel['id'],
			setId: TemplateSet['id'],
			options?: {
				marginAroundEdge?: number;
				marginBetweenPositions?: number;
				photoCount?: number;
				dpi?: number;
			},
		) => {
			const findTemplateSet = (dpi?: number) => this.getTemplateSets(
				templateId,
				{
					photoCount: options?.photoCount,
					marginAroundEdge: options?.marginAroundEdge,
					marginBetweenPositions: options?.marginBetweenPositions,
					dpi,
				},
			).find(
				(m) => m.id === setId,
			);
			const templateSet = findTemplateSet(options?.dpi);

			if (
				templateSet
				|| setId === DEFAULT_DYNAMIC_TEMPLATE_ID
				|| !ThemeDataModule.getTemplate(templateId)?.dynamic
			) {
				/**
				 * Fixed template sets and the full page default set do not depend
				 * on the dpi, so another dpi cannot resolve them either.
				 */
				return templateSet;
			}

			/**
			 * Dynamic position ids depend on the dpi the sets were built with.
			 * The layout pickers and new dynamic sets use the default dpi, while
			 * lookups use the page dpi, and older ids may come from sets built at
			 * another offering dpi. Try the default dpi first, then the dpis
			 * already built for this template, then the dpi of every loaded
			 * offering (the cache is empty after a reload). Ids hash the position
			 * geometry, so a match at another dpi is always the set the id was
			 * saved from.
			 */
			const fallbackDpis = _.uniq([
				undefined,
				...this.themeTemplateSets
					.filter((themeTemplateSet) => (
						themeTemplateSet.templateId === templateId
						&& themeTemplateSet.photoCount === options?.photoCount
						&& themeTemplateSet.marginAroundEdge === options?.marginAroundEdge
						&& themeTemplateSet.marginBetweenPositions === options?.marginBetweenPositions
					))
					.map((themeTemplateSet) => themeTemplateSet.dpi),
				...this.offeringConfigDpis,
			]).filter((dpi) => (
				// Sets built without a dpi use the default 300 dpi
				(dpi ?? 300) !== (options?.dpi ?? 300)
			));

			for (const dpi of fallbackDpis) {
				const fallbackTemplateSet = findTemplateSet(dpi);

				if (fallbackTemplateSet) {
					return fallbackTemplateSet;
				}
			}

			/*
			 * Old projects saved geometry hashes before margins and coordinates were
			 * converted consistently. Only an exact whole-layout match can recover
			 * that design. Keep these candidates out of modern caches and pickers;
			 * the project-load/preservation path retains the complete matched set.
			 */
			const templateModel = ThemeDataModule.getTemplate(templateId)!;
			const legacyDpis = _.uniq([
				options?.dpi ?? 300,
				...fallbackDpis.map((dpi) => dpi ?? 300),
			]).filter((dpi) => dpi !== 300);

			for (const dpi of legacyDpis) {
				const legacyTemplate = Template.getLegacyDynamicTemplate(
					{
						templateData: {
							id: templateModel.id,
							width: templateModel.width!,
							height: templateModel.height!,
							bleedMargin: templateModel.bleedmargin ?? 0,
							marginAroundEdge: options?.marginAroundEdge ?? templateModel.dynamicPageMargin ?? 0,
							marginBetweenPositions: options?.marginBetweenPositions ?? templateModel.dynamicPagePadding ?? 0,
						},
						...(options?.photoCount ? {
							positionQuantities: [options.photoCount],
						} : {}),
						dpi,
					},
					setId,
				);

				if (legacyTemplate) {
					return createDynamicTemplateSet(
						templateId,
						legacyTemplate,
					);
				}
			}

			return undefined;
		};
	}

	public get themeHasDynamicLayout(): boolean {
		return Boolean(this.themeModel?.dynamicLayout);
	}

	public get themeHasLayoutSelect() {
		return this.themeModel && this.themeModel.layoutselect;
	}

	public get themeHasPhotoSelect() {
		return this.themeModel && this.themeModel.photoselect;
	}

	@Mutation
	private _addLayout(data: DB.LayoutModel) {
		this.layoutCollection.push(data);
	}

	@Mutation
	private _addLayoutTemplateLink(data: DB.LayoutTemplateLinkModel) {
		this.layoutTemplateLinkCollection.push(data);
	}

	@Mutation
	private _addPageState(data: PI.PageStateModel) {
		this.pageStateCollection.push(data);
	}

	@Mutation
	private _addThemeLayoutLink(data: DB.ThemeLayoutLinkModel) {
		this.themeLayoutLinkCollection.push(data);
	}

	@Mutation
	private _resetBorderImages() {
		this.borderImageCollection = [];
	}

	@Mutation
	private _resetLayouts() {
		this.layoutCollection = [];
	}

	@Mutation
	private _resetLayoutTemplateLinks() {
		this.layoutTemplateLinkCollection = [];
	}

	@Mutation
	private _resetPages() {
		this.pageStateCollection = [];
	}

	@Mutation
	private _resetTheme() {
		this.themeModel = null;
	}

	@Mutation
	private _resetThemeLayoutLinks() {
		this.themeLayoutLinkCollection = [];
	}

	@Mutation
	private _resetThemeTemplateSets() {
		this.themeTemplateSets = [];
	}

	@Mutation
	private _setTheme(data: DB.ThemeModel) {
		this.themeModel = reactive(data);
	}

	@Action
	private _addTemplateState(data: TemplateStateModel) {
		const { getters, commit } = this.context;

		return new Promise((resolve, reject) => {
			if (!data.id) {
				reject(new Error(ERRORS_INVALID_REQUEST_DATA));
			} else {
				if (getters.getTemplateSetById(data.id)) {
					commit(
						'_setTemplateState',
						data,
					);
				} else {
					commit(
						'_pushTemplateState',
						data,
					);
				}

				resolve(getters.getTemplateSetById(data.id));
			}
		});
	}

	@Action
	public reset({ themeid }: {
		themeid?: number;
	} = {}): Promise<void> {
		this._resetBorderImages();
		this._resetLayouts();
		this._resetPages();
		this._resetTheme();
		this._resetThemeTemplateSets();
		this._resetThemeLayoutLinks();
		this._resetLayoutTemplateLinks();

		if (themeid) {
			ThemeDataModule.unloadTheme(themeid);
		}

		return Promise.resolve();
	}

	@Action
	public async setupData({ themeid }: {
		themeid: DB.ThemeModel['id'];
	}): Promise<void> {
		await this.reset();

		const themeModel = ThemeDataModule.getTheme(themeid);

		if (themeModel) {
			this._setTheme(themeModel);
		}

		const templateIds: Set<DB.TemplateModel['id']> = new Set();
		ThemeDataModule
			.whereThemePageLink({
				themeid,
			})
			.forEach((themePageModel) => {
				const pageModel = ThemeDataModule.getPage(themePageModel.pageid);

				if (pageModel && pageModel.id) {
					const pageStateData: PI.PageStateModel = {
						id: pageModel.id,
						objects: [],
						pageModel,
						repeat: themePageModel.repeat,
						scaling: 0.5,
						serialnumber: pageModel.serialnumber || 0,
					};

					if (pageModel.template) {
						templateIds.add(pageModel.template);
					}

					ThemeDataModule.whereObjects({
						pageid: pageModel.id,
					}).forEach((objectModel) => {
						let objectData = JSON.parse(JSON.stringify(objectModel));

						if (objectModel.type == 'photo') {
							const photoObjectModel = ThemeDataModule.findWherePhotoObjects({
								objectid: objectModel.id,
							});

							if (photoObjectModel) {
								objectData = _.extend(
									objectData,
									JSON.parse(JSON.stringify(photoObjectModel)),
									{
										id: objectModel.id,
									},
								);
							}
						} else if (objectModel.type == 'text') {
							const textObjectModel = ThemeDataModule.findWhereTextObjects({
								objectid: objectModel.id,
							});

							if (textObjectModel) {
								objectData = _.extend(
									objectData,
									JSON.parse(JSON.stringify(textObjectModel)),
									{
										id: objectModel.id,
									},
								);
							}
						}

						pageStateData.objects.push(objectData);
					});

					this._addPageState(pageStateData);
				}
			});

		const themeLayoutLinks = ThemeDataModule.findThemeLayoutLinks({
			themeid,
		});
		themeLayoutLinks.forEach((themeLayoutLink) => {
			this._addThemeLayoutLink(themeLayoutLink);

			const layout = ThemeDataModule.getLayoutById(themeLayoutLink.layoutid);

			if (layout) {
				this._addLayout(layout);
			}

			const layoutTemplateLinks = ThemeDataModule.findLayoutTemplateLinks({
				layoutid: themeLayoutLink.layoutid,
			});
			layoutTemplateLinks.forEach((layoutTemplateLink) => {
				this._addLayoutTemplateLink(layoutTemplateLink);

				if (layoutTemplateLink.templateid) {
					templateIds.add(layoutTemplateLink.templateid);
				}
			});
		});
	}
}
